import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { 
  TrendingUp, TrendingDown, Minus, DollarSign, Users, 
  Settings, GraduationCap, BarChart3, PieChart, Gauge,
  Edit3, Plus, GripVertical, X, Check, LayoutDashboard
} from 'lucide-react';
import { 
  bscPerspectives, mockObjectives, mockKPITrends,
  calculateEntityScore, calculatePerspectiveScore 
} from '../../mock/strategyData';
import SectionHeader from '../shared/SectionHeader';

// KPI Card Widget
const KPICardWidget = ({ title, actual, target, unit, trend, achievement, color }) => {
  const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : Minus;
  const trendColor = trend === 'up' ? 'text-emerald-500' : trend === 'down' ? 'text-red-500' : 'text-muted-foreground';
  
  const getAchievementColor = (ach) => {
    if (ach >= 100) return 'text-emerald-500';
    if (ach >= 90) return 'text-blue-500';
    if (ach >= 75) return 'text-amber-500';
    return 'text-red-500';
  };

  return (
    <Card className="relative overflow-hidden group hover:shadow-lg transition-all">
      <div className="absolute top-0 right-0 w-24 h-24 opacity-10">
        <div 
          className="w-full h-full rounded-bl-full"
          style={{ backgroundColor: color }}
        />
      </div>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{title}</p>
            <div className="mt-1">
              <span className="text-2xl font-bold text-foreground">{actual}</span>
              <span className="text-lg text-muted-foreground ml-1">{unit}</span>
            </div>
          </div>
          <div className={`flex items-center gap-1 ${trendColor}`}>
            <TrendIcon className="w-4 h-4" />
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Target: {target}{unit}</span>
          <span className={`font-semibold ${getAchievementColor(achievement)}`}>{achievement}%</span>
        </div>
        <Progress 
          value={Math.min(achievement, 100)} 
          className="mt-2 h-1.5"
          indicatorClassName="bg-gradient-to-r"
          style={{ '--tw-gradient-from': color, '--tw-gradient-to': color }}
        />
      </CardContent>
    </Card>
  );
};

// Mini Chart Component (simplified bar chart)
const MiniBarChart = ({ data, color }) => {
  const maxValue = Math.max(...data.map(d => Math.max(d.actual, d.target)));
  
  return (
    <div className="flex items-end justify-between gap-1 h-32">
      {data.map((item, index) => (
        <div key={index} className="flex-1 flex flex-col items-center gap-1">
          <div className="w-full flex gap-0.5 items-end h-24">
            <div 
              className="flex-1 rounded-t transition-all"
              style={{ 
                height: `${(item.actual / maxValue) * 100}%`,
                backgroundColor: color
              }}
            />
            <div 
              className="flex-1 rounded-t bg-muted/50 border border-dashed border-muted-foreground/30"
              style={{ height: `${(item.target / maxValue) * 100}%` }}
            />
          </div>
          <span className="text-[10px] text-muted-foreground">{item.month.slice(0, 3)}</span>
        </div>
      ))}
    </div>
  );
};

// Gauge Widget
const GaugeWidget = ({ score, label }) => {
  const getColor = (score) => {
    if (score >= 90) return '#10b981';
    if (score >= 75) return '#3b82f6';
    if (score >= 60) return '#f59e0b';
    return '#ef4444';
  };

  const color = getColor(score);
  const rotation = (score / 100) * 180 - 90;

  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-sm font-medium text-muted-foreground text-center mb-4">{label}</p>
        <div className="relative w-32 h-16 mx-auto overflow-hidden">
          {/* Background arc */}
          <div 
            className="absolute inset-0 rounded-t-full border-8 border-muted/30"
            style={{ borderBottomWidth: 0 }}
          />
          {/* Colored arc */}
          <div 
            className="absolute inset-0 rounded-t-full border-8 border-transparent"
            style={{ 
              borderTopColor: color,
              borderLeftColor: score > 25 ? color : 'transparent',
              borderRightColor: score > 75 ? color : 'transparent',
              borderBottomWidth: 0,
              transform: `rotate(${Math.min(score * 1.8, 180) - 90}deg)`,
              transformOrigin: 'bottom center'
            }}
          />
          {/* Center */}
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 text-center">
            <span className="text-2xl font-bold" style={{ color }}>{score}%</span>
          </div>
        </div>
        <div className="flex justify-between text-xs text-muted-foreground mt-2">
          <span>0%</span>
          <span>50%</span>
          <span>100%</span>
        </div>
      </CardContent>
    </Card>
  );
};

// Donut Chart Widget
const DonutWidget = ({ data, title }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  let cumulativePercent = 0;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-4">
          <div className="relative w-24 h-24">
            <svg viewBox="0 0 36 36" className="w-full h-full transform -rotate-90">
              {data.map((item, index) => {
                const percent = (item.value / total) * 100;
                const dashArray = `${percent} ${100 - percent}`;
                const dashOffset = -cumulativePercent;
                cumulativePercent += percent;
                
                return (
                  <circle
                    key={index}
                    cx="18"
                    cy="18"
                    r="14"
                    fill="none"
                    stroke={item.color}
                    strokeWidth="4"
                    strokeDasharray={dashArray}
                    strokeDashoffset={dashOffset}
                  />
                );
              })}
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-xs font-bold text-foreground">{total.toFixed(0)}M</span>
            </div>
          </div>
          <div className="flex-1 space-y-1">
            {data.map((item, index) => (
              <div key={index} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className="text-muted-foreground">{item.label}</span>
                </div>
                <span className="font-medium text-foreground">{item.value}M</span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Performance Table Widget
const PerformanceTableWidget = ({ objectives }) => {
  const topObjectives = objectives.slice(0, 5);
  
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">Top KPIs Performance</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {topObjectives.map((obj, index) => {
            const achievement = obj.target > 0 ? Math.round((obj.actual / obj.target) * 100) : 0;
            const getColor = (ach) => {
              if (ach >= 100) return 'bg-emerald-500';
              if (ach >= 90) return 'bg-blue-500';
              if (ach >= 75) return 'bg-amber-500';
              return 'bg-red-500';
            };
            
            return (
              <div key={obj.id} className="flex items-center gap-3">
                <span className="w-4 text-xs text-muted-foreground">{index + 1}</span>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-foreground truncate">{obj.name}</span>
                    <span className="text-xs font-bold text-foreground">{achievement}%</span>
                  </div>
                  <Progress value={Math.min(achievement, 100)} className="h-1.5" indicatorClassName={getColor(achievement)} />
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

const StrategyDashboard = ({ entity }) => {
  const [isEditing, setIsEditing] = useState(false);

  // Get entity objectives
  const entityObjectives = useMemo(() => {
    return mockObjectives.filter(
      obj => obj.entityId === entity.id && obj.entityType === entity.entityType
    );
  }, [entity]);

  // Calculate scores
  const overallScore = useMemo(() => calculateEntityScore(entity.id, entity.entityType), [entity]);
  const perspectiveScores = useMemo(() => {
    const scores = {};
    bscPerspectives.forEach(p => {
      scores[p.id] = calculatePerspectiveScore(entity.id, entity.entityType, p.id);
    });
    return scores;
  }, [entity]);

  // Get financial objectives for KPI cards
  const financialKPIs = entityObjectives.filter(obj => obj.perspectiveId === 'financial');

  // Mock revenue breakdown
  const revenueBreakdown = [
    { label: 'Products', value: 180, color: '#10b981' },
    { label: 'Services', value: 120, color: '#3b82f6' },
    { label: 'Consulting', value: 45, color: '#f59e0b' },
    { label: 'Other', value: 13.8, color: '#8b5cf6' }
  ];

  // Get trend data (use mock if available, otherwise generate)
  const trendData = mockKPITrends[entity.id]?.revenue || [
    { month: 'Jan', actual: 28, target: 30 },
    { month: 'Feb', actual: 26, target: 30 },
    { month: 'Mar', actual: 29, target: 30 },
    { month: 'Apr', actual: 31, target: 30 },
    { month: 'May', actual: 28, target: 30 },
    { month: 'Jun', actual: 32, target: 30 },
    { month: 'Jul', actual: 30, target: 30 },
    { month: 'Aug', actual: 33, target: 30 },
    { month: 'Sep', actual: 35, target: 30 },
    { month: 'Oct', actual: 32, target: 30 },
    { month: 'Nov', actual: 29, target: 30 },
    { month: 'Dec', actual: 31, target: 30 }
  ];

  return (
    <div className="space-y-6">
      {/* Dashboard Header */}
      <SectionHeader
        title={`${entity.name} Dashboard`}
        icon={LayoutDashboard}
        iconColor="text-purple-500"
        actions={
          <Button 
            variant={isEditing ? 'default' : 'outline'}
            onClick={() => setIsEditing(!isEditing)}
            data-testid="edit-dashboard-btn"
          >
            {isEditing ? (
              <>
                <Check className="w-4 h-4 mr-2" />
                Done
              </>
            ) : (
              <>
                <Edit3 className="w-4 h-4 mr-2" />
                Edit Dashboard
              </>
            )}
          </Button>
        }
      />

      {/* KPI Cards Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {financialKPIs.slice(0, 5).map((kpi, index) => {
          const achievement = kpi.target > 0 ? Math.round((kpi.actual / kpi.target) * 100) : 0;
          const colors = ['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6', '#ec4899'];
          
          return (
            <KPICardWidget
              key={kpi.id}
              title={kpi.name}
              actual={kpi.actual}
              target={kpi.target}
              unit={kpi.unit}
              trend={kpi.trend}
              achievement={achievement}
              color={colors[index % colors.length]}
            />
          );
        })}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue Performance Chart */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium">Revenue Performance (Actual vs Target)</CardTitle>
              <div className="flex items-center gap-4 text-xs">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded bg-emerald-500" />
                  <span className="text-muted-foreground">Actual</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded border border-dashed border-muted-foreground" />
                  <span className="text-muted-foreground">Target</span>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <MiniBarChart data={trendData} color="#10b981" />
          </CardContent>
        </Card>

        {/* Profitability Summary */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Profitability & Performance</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {entityObjectives
              .filter(obj => obj.perspectiveId === 'financial')
              .slice(0, 3)
              .map(obj => {
                const achievement = obj.target > 0 ? Math.round((obj.actual / obj.target) * 100) : 0;
                return (
                  <div key={obj.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                    <div>
                      <p className="text-xs text-muted-foreground">{obj.name}</p>
                      <p className="text-lg font-bold text-foreground">{obj.actual}{obj.unit}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">Achievement</p>
                      <p className={`text-lg font-bold ${achievement >= 90 ? 'text-emerald-500' : achievement >= 75 ? 'text-amber-500' : 'text-red-500'}`}>
                        {achievement}%
                      </p>
                    </div>
                  </div>
                );
              })}
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Overall Achievement Gauge */}
        <GaugeWidget score={overallScore} label="Overall Achievement" />

        {/* Revenue Breakdown */}
        <DonutWidget data={revenueBreakdown} title="Revenue Breakdown" />

        {/* Top KPIs Table */}
        <div className="lg:col-span-2">
          <PerformanceTableWidget objectives={entityObjectives} />
        </div>
      </div>

      {/* Perspective Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {bscPerspectives.map(perspective => {
          const perspectiveObjs = entityObjectives.filter(obj => obj.perspectiveId === perspective.id);
          const exceedingCount = perspectiveObjs.filter(obj => obj.status === 'exceeding').length;
          const atRiskCount = perspectiveObjs.filter(obj => ['at-risk', 'below-target', 'critical'].includes(obj.status)).length;
          
          return (
            <Card key={perspective.id} className="overflow-hidden">
              <div className="h-1" style={{ backgroundColor: perspective.color }} />
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-muted-foreground">{perspective.name}</span>
                  <span className="text-lg font-bold text-foreground">{perspectiveScores[perspective.id]}%</span>
                </div>
                <Progress 
                  value={perspectiveScores[perspective.id]} 
                  className="h-2 mb-2"
                  indicatorClassName="transition-all"
                  style={{ backgroundColor: `${perspective.color}30` }}
                />
                <div className="flex items-center justify-between text-xs">
                  <span className="text-emerald-500">{exceedingCount} exceeding</span>
                  <span className="text-amber-500">{atRiskCount} at risk</span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {isEditing && (
        <Card className="border-dashed border-2 border-primary/50 bg-primary/5">
          <CardContent className="p-8 text-center">
            <Plus className="w-8 h-8 mx-auto text-primary mb-2" />
            <p className="text-sm font-medium text-foreground">Add Widget</p>
            <p className="text-xs text-muted-foreground">Click to add a new KPI widget to your dashboard</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default StrategyDashboard;
